/**
 * Fio → tela do retorno da autorização (`TP-056`). Um corpo fora do contrato vira erro na fronteira, nunca célula
 * vazia lá na frente — mesma disciplina dos outros mapeadores.
 */

import type { CaseReturnsView, CaseState, PendingReturnItem } from '@anery/api-contracts';

import { ErroDaApi } from '../api/erros';
import { parseInstante, parseInstanteOuNulo } from '../dominio/datas';
import type {
  DecisaoDoRetorno,
  MotivoDoDescarte,
  OrigemDoResultado,
  PropostaDoRetorno,
  ResultadoDoRetorno,
  RetornoAConfirmar,
  RetornoDoCaso,
  RetornosDoCaso,
} from './tipos';

const RESULTADOS: readonly ResultadoDoRetorno[] = [
  'Authorized',
  'FirstDayReleased',
  'Extended',
  'PendingDocuments',
  'Denied',
];
const ORIGENS: readonly OrigemDoResultado[] = ['Agent', 'Human'];
const DECISOES: readonly DecisaoDoRetorno[] = ['Confirmed', 'Discarded'];
const MOTIVOS: readonly MotivoDoDescarte[] = ['WrongReading', 'AlreadyHandled', 'NotAReturn', 'Other'];
const PROPOSTAS: readonly PropostaDoRetorno[] = ['Authorize', 'Decline', 'OpenQuestion', 'RecordOnly'];
const ESTADOS: readonly CaseState[] = [
  'Received',
  'UnderAssessment',
  'QuoteIssued',
  'AwaitingPayer',
  'Authorized',
  'Declined',
  'Closed',
];

function erroDeContrato(campo: string, valor: unknown): ErroDaApi {
  return new ErroDaApi({
    origem: 'formato',
    mensagem: `Resposta fora do contrato em ${campo}: ${JSON.stringify(valor)}`,
  });
}

function umDe<T extends string>(lista: readonly T[], valor: unknown, campo: string): T {
  if (typeof valor === 'string' && (lista as readonly string[]).includes(valor)) return valor as T;
  throw erroDeContrato(campo, valor);
}

function umDeOuNulo<T extends string>(lista: readonly T[], valor: unknown, campo: string): T | null {
  return valor === null || valor === undefined ? null : umDe(lista, valor, campo);
}

function confianca(valor: unknown): number | null {
  return typeof valor === 'number' ? valor : null;
}

export function mapearRetornosAConfirmar(itens: readonly PendingReturnItem[]): readonly RetornoAConfirmar[] {
  return itens.map((item, i) => {
    const campo = `[${String(i)}]`;
    return {
      emailId: item.emailId,
      casoId: item.caseId,
      paciente: item.patientName ?? null,
      estadoDoCaso: umDe(ESTADOS, item.caseState, `${campo}.caseState`),
      dono: item.ownerName ?? null,
      resultado: umDe(RESULTADOS, item.result, `${campo}.result`),
      origem: umDe(ORIGENS, item.source, `${campo}.source`),
      confianca: confianca(item.confidence),
      recebidoEm: parseInstante(item.receivedAt),
      proposta: umDe(PROPOSTAS, item.proposal, `${campo}.proposal`),
      efetivoEm: parseInstanteOuNulo(item.effectiveAt),
    };
  });
}

export function mapearRetornosDoCaso(view: CaseReturnsView): RetornosDoCaso {
  const vez = view.aneryTurn ?? null;
  return {
    vezDaAnery:
      vez === null
        ? null
        : {
            inicio: parseInstante(vez.startedAt),
            fim: parseInstanteOuNulo(vez.endedAt),
            duracaoMinutos: vez.durationMinutes,
          },
    retornos: (view.returns ?? []).map((r, i): RetornoDoCaso => {
      const campo = `returns[${String(i)}]`;
      return {
        emailId: r.emailId,
        recebidoEm: parseInstante(r.receivedAt),
        resultado: umDe(RESULTADOS, r.result, `${campo}.result`),
        origem: umDe(ORIGENS, r.source, `${campo}.source`),
        confianca: confianca(r.confidence),
        decisao: umDeOuNulo(DECISOES, r.decision, `${campo}.decision`),
        decididoEm: parseInstanteOuNulo(r.decidedAt),
        motivoDoDescarte: umDeOuNulo(MOTIVOS, r.discardReason, `${campo}.discardReason`),
        respondidoEm: parseInstanteOuNulo(r.questionAnsweredAt),
        proposta: umDeOuNulo(PROPOSTAS, r.proposal, `${campo}.proposal`),
        efetivoEm: parseInstanteOuNulo(r.effectiveAt),
      };
    }),
  };
}
